import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'

import { api, onSessionExpired } from './api'
import { setLang, t } from './i18n'
import CreateGame from './screens/CreateGame'
import Feed from './screens/Feed'
import GameDetail from './screens/GameDetail'
import Onboarding from './screens/Onboarding'
import Profile from './screens/Profile'
import BookingScreen, { type BookTarget } from './screens/Booking'
import ClubDesk from './screens/ClubDesk'
import MyBookings from './screens/MyBookings'
import GenderPrompt from './components/GenderPrompt'
import InvitePopup from './components/InvitePopup'
import { isTelegramApp, needsLogin, setAndroidHome } from './platform'
import { currentBack, subscribeBack } from './telegram'
import Login from './screens/Login'
import { ensureGender } from './gender'
import {
  askWriteAccess,
  canBotWrite,
  haptic,
  initialBookClub,
  initialDesk,
  initialGameId,
  initialInviterId,
  initialMyBookings,
  notify,
} from './telegram'
import type { GameKind, MyInvite, PlayerMe } from './types'

type Tab = 'feed' | 'create' | 'profile'

export default function App() {
  const [me, setMe] = useState<PlayerMe | null>(null)
  const [tab, setTab] = useState<Tab>('feed')
  // С каким форматом открыть создание: тренер приходит из профиля за тренировкой
  const [createKind, setCreateKind] = useState<GameKind>('game')
  const openCreate = (kind: GameKind = 'game') => {
    setCreateKind(kind)
    setTab('create')
  }
  const [error, setError] = useState<string | null>(null)
  // Пришли по ссылке из группы или из уведомления бота — сразу открываем игру.
  // Если игрок новый, id переживёт онбординг и откроется после него.
  const [openGameId, setOpenGameId] = useState<number | null>(initialGameId)

  // Заявки на корт (v0.35): экран брони, мои брони, кабинет клуба — окна
  // поверх любого экрана. Открываются и по ссылкам (startapp=b<клуб>, ?desk=…)
  const [bookTarget, setBookTarget] = useState<BookTarget | null>(() => {
    const club = initialBookClub()
    return club ? { clubId: club } : null
  })
  const [deskClub, setDeskClub] = useState<number | null>(initialDesk)
  const [myBookings, setMyBookings] = useState(initialMyBookings)

  // Вне Telegram (нативное приложение) сначала нужен вход
  const [login, setLogin] = useState(needsLogin)

  const load = useCallback(async () => {
    if (needsLogin()) {
      setLogin(true)
      return
    }
    setLogin(false)
    try {
      const player = await api.me()
      setLang(player.language)
      setMe(player)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!isTelegramApp) document.documentElement.classList.add('standalone')
    // Сервер отверг токен (вышли на другом устройстве, аккаунт удалён) — на экран входа
    onSessionExpired(() => {
      setMe(null)
      setLogin(true)
    })
  }, [])

  // Без права писать в личку игрок не получит ни одного напоминания.
  // Онбординг спрашивает об этом один раз, но после переезда на нового бота
  // и у тех, кто тогда отказался, флаг false — спрашиваем снова, раз за сессию.
  const onboarded = me?.level != null
  useEffect(() => {
    if (onboarded && !canBotWrite()) void askWriteAccess()
  }, [onboarded])

  // --- Приглашения: всплывают при входе и при возврате в приложение ---
  const [invites, setInvites] = useState<MyInvite[]>([])
  const [snoozed, setSnoozed] = useState<number[]>([])
  // Меняется, когда экран игры нужно перечитать (привязали личную ссылку)
  const [gameKey, setGameKey] = useState(0)

  const loadInvites = useCallback(async () => {
    try {
      setInvites(await api.myInvites())
    } catch {
      /* без приглашений приложение работает как обычно */
    }
  }, [])

  useEffect(() => {
    if (!onboarded) return
    const inviter = initialInviterId()
    const linked = initialGameId()
    // Пришли по личной ссылке: запоминаем, кто позвал, — экран игры покажет
    // «Игорь зовёт вас», а Игорь узнает, когда человек запишется
    const claimed =
      inviter && linked
        ? api.claimInvite(linked, inviter).then(() => setGameKey((k) => k + 1), () => undefined)
        : Promise.resolve()
    void claimed.then(loadInvites)

    const onVisible = () => {
      if (document.visibilityState === 'visible') void loadInvites()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [onboarded, loadInvites])

  const dropInvite = (id: number) => setInvites((list) => list.filter((i) => i.id !== id))

  // Окно всплывает только на главных экранах. Пока открыта игра, человек занят
  // ею: приглашение в неё показано баннером, остальные подождут возврата в ленту.
  const popupInvites = invites.filter((i) => !snoozed.includes(i.id))
  const popup = popupInvites.length > 0 && (
    <InvitePopup
      key={popupInvites[0].id}
      invites={popupInvites}
      onAccept={async (invite) => {
        haptic('medium')
        try {
          if (invite.game.mixed && me && !(await ensureGender(me))) return
          await api.join(invite.game.id)
          dropInvite(invite.id)
          setOpenGameId(invite.game.id)
        } catch (e) {
          notify(e instanceof Error ? e.message : 'Ошибка')
          void loadInvites()
        }
      }}
      onDecline={async (invite) => {
        try {
          await api.declineInvite(invite.id)
        } catch {
          /* приглашение уже неактуально */
        }
        dropInvite(invite.id)
      }}
      onDetails={(invite) => setOpenGameId(invite.game.id)}
      onLater={(invite) => setSnoozed((list) => [...list, invite.id])}
    />
  )

  const closeGame = useCallback(() => {
    setOpenGameId(null)
    void loadInvites()
  }, [loadInvites])

  // Android: системная «Назад» с вкладок «Создать» и «Профиль» — на ленту
  useEffect(() => {
    setAndroidHome(tab !== 'feed' && openGameId === null ? () => setTab('feed') : null)
    return () => setAndroidHome(null)
  }, [tab, openGameId])

  if (login) {
    return (
      <div className="wrap">
        <Login onDone={load} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="wrap">
        <div className="card">
          <div className="title">Не удалось войти</div>
          <p className="muted">{error}</p>
          <button onClick={() => window.location.reload()}>Попробовать снова</button>
        </div>
      </div>
    )
  }

  if (!me) return <div className="wrap muted">{t().loading}</div>

  // Онбординг обязателен: без уровня подбор игр не работает
  if (me.level === null) {
    return (
      <div className="wrap">
        <Onboarding city={me.city} onDone={setMe} />
      </div>
    )
  }

  const refreshMe = () => {
    void api.me().then(setMe, () => undefined)
  }
  const overlays = (
    <>
      {bookTarget && (
        <BookingScreen
          key={`${bookTarget.clubId}-${bookTarget.gameId ?? ''}`}
          target={bookTarget}
          onClose={() => {
            setBookTarget(null)
            refreshMe()
          }}
          onMine={() => {
            setBookTarget(null)
            setMyBookings(true)
          }}
        />
      )}
      {myBookings && !bookTarget && (
        <MyBookings
          onClose={() => {
            setMyBookings(false)
            refreshMe()
          }}
          onBook={(clubId) => {
            setMyBookings(false)
            setBookTarget({ clubId })
          }}
        />
      )}
      {deskClub !== null && !bookTarget && !myBookings && (
        <ClubDesk
          clubId={deskClub}
          onClose={() => {
            setDeskClub(null)
            refreshMe()
          }}
        />
      )}
    </>
  )

  if (openGameId !== null) {
    return (
      <div className="wrap">
        <BackBar />
        <GameDetail
          key={gameKey}
          gameId={openGameId}
          me={me}
          onBack={closeGame}
          onBook={setBookTarget}
          covered={bookTarget !== null || myBookings || deskClub !== null}
        />
        <GenderPrompt onSaved={load} />
        {overlays}
      </div>
    )
  }

  return (
    <>
      <div className="wrap">
        <BackBar />
        {tab === 'feed' && (
          <Feed me={me} onCreate={() => openCreate()} onOpen={setOpenGameId} onBook={(clubId) => setBookTarget({ clubId })} />
        )}
        {tab === 'create' && (
          <CreateGame
            key={createKind}
            me={me}
            initialKind={createKind}
            onCreated={(id) => {
              // Капитан попадает на экран своей игры с кнопкой «Поделиться» —
              // следующий естественный шаг: кинуть ссылку в группу.
              setTab('feed')
              setOpenGameId(id)
            }}
            onCancel={() => setTab('feed')}
          />
        )}
        {tab === 'profile' && (
          <Profile
            me={me}
            onChange={setMe}
            onOpenGame={setOpenGameId}
            onCreateTraining={() => openCreate('training')}
            onMyBookings={() => setMyBookings(true)}
            onDesk={setDeskClub}
          />
        )}
      </div>

      {popup}
      <GenderPrompt onSaved={load} />
      {overlays}

      <nav className="tabs">
        <button aria-current={tab === 'feed'} onClick={() => setTab('feed')}>
          <span className="tab-ico" aria-hidden>
            🎾
          </span>
          {t().feed}
        </button>
        {/* Создать игру — второе главное действие: крупная кнопка по центру */}
        <button className="tab-plus" aria-label={t().create} aria-current={tab === 'create'} onClick={() => openCreate()}>
          <span className="plus">+</span>
        </button>
        <button aria-current={tab === 'profile'} onClick={() => setTab('profile')}>
          <span className="tab-ico" aria-hidden>
            👤
          </span>
          {t().profile}
        </button>
      </nav>
    </>
  )
}

/** Вне Telegram системной кнопки «Назад» нет — рисуем свою. */
function BackBar() {
  const back = useSyncExternalStore(subscribeBack, currentBack)
  if (isTelegramApp || !back) return null
  return (
    <button className="backbar" onClick={back}>
      ‹ {t().back}
    </button>
  )
}
